import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Pencil, Trash2, CheckCircle2, RefreshCw, Wallet, X, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";

const formatMesLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${months[parseInt(m, 10) - 1]}/${y}`;
};

export default function Commissions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editValor, setEditValor] = useState(0);
  const [mes, setMes] = useState("");
  const [vendedorId, setVendedorId] = useState("");
  const [statusF, setStatusF] = useState("pendente");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ vendedor_id: "", valor: 0, data: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    const params = {};
    if (mes) params.mes = mes;
    if (statusF) params.status = statusF;
    if (vendedorId) params.vendedor_id = vendedorId;
    const [c, g, s, a] = await Promise.all([
      api.get("/commissions", { params }),
      api.get("/commissions/grouped", { params: { status: statusF || "pendente", mes } }),
      api.get("/sellers"),
      api.get("/commissions/advances").catch(() => ({ data: [] })),
    ]);
    setItems(c.data); setGrouped(g.data); setSellers(s.data); setAdvances(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mes, vendedorId, statusF]);

  // Build list of months from all loaded data (so user has dropdown)
  const monthOptions = useMemo(() => {
    const set = new Set();
    items.forEach(c => c.mes_apuracao && set.add(c.mes_apuracao));
    grouped.forEach(g => g.mes && set.add(g.mes));
    const arr = Array.from(set).sort().reverse();
    // Include current month always
    const now = new Date();
    const cm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (!arr.includes(cm)) arr.unshift(cm);
    return arr;
  }, [items, grouped]);

  const recompute = async () => {
    try { await api.post("/recompute"); toast.success("Recálculo concluído"); load(); }
    catch { toast.error("Erro ao recalcular"); }
  };

  const pay = async (id) => {
    try { await api.post(`/commissions/${id}/pay`); toast.success("Comissão paga"); load(); }
    catch { toast.error("Erro"); }
  };

  const payAll = async (vendedor_id, monthFilter) => {
    const lbl = [vendedor_id ? "deste vendedor" : "todos vendedores", monthFilter ? `no mês ${formatMesLabel(monthFilter)}` : ""].filter(Boolean).join(" ");
    if (!window.confirm(`Pagar TODAS as comissões pendentes (${lbl})?`)) return;
    try {
      const { data } = await api.post("/commissions/pay-all", { vendedor_id, mes: monthFilter });
      toast.success(`${data.count} comissões pagas — ${fmtBRL(data.total)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openEdit = (c) => { setEdit(c); setEditValor(c.valor); };
  const saveEdit = async () => {
    try {
      await api.put(`/commissions/${edit.id}`, { valor: parseFloat(editValor) });
      toast.success("Comissão atualizada"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Excluir comissão de ${fmtBRL(c.valor)}?`)) return;
    try { await api.delete(`/commissions/${c.id}`); toast.success("Excluída"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openManual = () => { setManualForm({ vendedor_id: vendedorId || "", valor: 0, data: new Date().toISOString().slice(0, 10) }); setManualOpen(true); };
  const submitManual = async () => {
    if (!manualForm.vendedor_id) return toast.error("Selecione o vendedor");
    if (!manualForm.valor || Number(manualForm.valor) <= 0) return toast.error("Informe um valor válido");
    try {
      const { data } = await api.post("/commissions/manual-pay", { vendedor_id: manualForm.vendedor_id, valor: parseFloat(manualForm.valor), data: manualForm.data });
      let msg = `${data.comissoes_pagas} comissão(ões) paga(s) — ${fmtBRL(data.valor_usado)}`;
      if (data.valor_adiantado > 0) msg += ` + ${fmtBRL(data.valor_adiantado)} adiantado (será abatido de próximas comissões)`;
      toast.success(msg);
      setManualOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const sn = (id) => sellers.find(s => s.id === id)?.nome || "—";
  // Totals for current filter
  const totalFiltrado = items.filter(c => c.status === "pendente").reduce((a, c) => a + c.valor, 0);
  const totalGrupos = grouped.reduce((a, g) => a + g.total, 0);
  const canEdit = can(user, "edit_commissions");
  const canPay = can(user, "pay_commissions");

  return (
    <Layout title="Comissões" subtitle="20% sobre valores recebidos (automático)">
      <Card className="p-4 rounded-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs font-semibold">Mês de apuração</Label>
            <SearchableSelect
              value={mes}
              onValueChange={setMes}
              options={monthOptions.map(m => ({ value: m, label: formatMesLabel(m) }))}
              placeholder="Todos os meses"
              searchPlaceholder="Buscar mês..."
              allowClear
              testid="comm-filter-mes"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Vendedor</Label>
            <SearchableSelect
              value={vendedorId}
              onValueChange={setVendedorId}
              options={sellers.map(s => ({ value: s.id, label: s.nome }))}
              placeholder="Todos"
              searchPlaceholder="Buscar vendedor..."
              allowClear
              testid="comm-filter-vendedor"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <SearchableSelect
              value={statusF}
              onValueChange={setStatusF}
              options={[
                { value: "pendente", label: "Pendente" },
                { value: "pago", label: "Pago" },
                { value: "cancelado", label: "Cancelado" },
              ]}
              placeholder="Todos"
              searchPlaceholder="Buscar..."
              allowClear
              testid="comm-filter-status"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 justify-end">
            {(mes || vendedorId || statusF !== "pendente") && (
              <Button variant="ghost" size="sm" onClick={() => { setMes(""); setVendedorId(""); setStatusF("pendente"); }} data-testid="comm-filter-clear">
                <X className="w-4 h-4 mr-1"/>Limpar
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={recompute} data-testid="comm-recompute">
                <RefreshCw className="w-4 h-4 mr-1"/>Recalcular
              </Button>
            )}
            {canPay && (
              <Button variant="outline" size="sm" onClick={openManual} data-testid="comm-manual-pay-open">
                <HandCoins className="w-4 h-4 mr-1"/>Pagar valor manual
              </Button>
            )}
          </div>
        </div>
      </Card>

      {advances.length > 0 && (
        <Card className="p-4 rounded-sm mb-4 border-amber-300 bg-amber-50/40" data-testid="comm-advances-card">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-2">Adiantamentos concedidos (a abater das próximas comissões)</p>
          <div className="flex flex-wrap gap-2">
            {advances.map(a => (
              <span key={a.vendedor_id} className="text-xs px-2 py-1 rounded-sm bg-amber-100 border border-amber-200 text-amber-800" data-testid={`comm-advance-${a.vendedor_id}`}>
                {sn(a.vendedor_id)}: <strong>-{fmtBRL(a.saldo)}</strong>
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 rounded-sm md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {mes ? `Total Pendente em ${formatMesLabel(mes)}` : "Total Pendente (todos os meses)"}
            {vendedorId && ` — ${sn(vendedorId)}`}
          </p>
          <p className="kpi-num text-3xl mt-2" data-testid="comm-total">{fmtBRL(statusF === "pendente" ? totalGrupos : totalFiltrado)}</p>
          {grouped.length > 0 && statusF === "pendente" && (
            <p className="text-xs text-muted-foreground mt-1">
              {grouped.length} grupo(s) — {grouped.reduce((a, g) => a + g.count, 0)} comissões
            </p>
          )}
        </Card>
        <Card className="p-4 rounded-sm flex flex-col justify-center gap-2">
          {canPay && grouped.length > 0 && statusF === "pendente" && (
            <Button onClick={() => payAll(vendedorId || null, mes || null)} data-testid="comm-pay-filtered" className="w-full">
              <Wallet className="w-4 h-4 mr-1"/>
              {mes || vendedorId ? "Pagar Total Filtrado" : "Pagar Todas Pendentes"}
            </Button>
          )}
          {canPay && grouped.length === 0 && statusF === "pendente" && (
            <p className="text-xs text-muted-foreground text-center">Nenhuma comissão pendente no filtro</p>
          )}
        </Card>
      </div>

      <Tabs defaultValue="grouped">
        <TabsList>
          <TabsTrigger value="grouped" data-testid="tab-grouped">Por Vendedor</TabsTrigger>
          <TabsTrigger value="detail" data-testid="tab-detail">Detalhado</TabsTrigger>
        </TabsList>

        <TabsContent value="grouped">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow>
                <TableHead>Vendedor</TableHead><TableHead>Mês Apuração</TableHead>
                <TableHead>Qtd</TableHead><TableHead>Total a Pagar</TableHead>
                <TableHead className="text-right">Ação</TableHead>
              </TableRow></TableHeader>
              <TableBody>{grouped.map((g, idx) => (
                <TableRow key={idx} data-testid={`comm-group-${g.vendedor_id}-${g.mes}`}>
                  <TableCell className="font-medium">{sn(g.vendedor_id)}</TableCell>
                  <TableCell>{formatMesLabel(g.mes)}</TableCell>
                  <TableCell>{g.count}</TableCell>
                  <TableCell className="font-semibold">{fmtBRL(g.total)}</TableCell>
                  <TableCell className="text-right">
                    {canPay && statusF === "pendente" && <Button data-testid={`comm-pay-group-${g.vendedor_id}-${g.mes}`} size="sm" variant="outline" onClick={() => payAll(g.vendedor_id, g.mes)}>Pagar Tudo</Button>}
                  </TableCell>
                </TableRow>
              ))}{grouped.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nenhum resultado para o filtro</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="detail">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow>
                <TableHead>Vendedor</TableHead><TableHead>Recebido em</TableHead><TableHead>Mês</TableHead>
                <TableHead>Base</TableHead><TableHead>Comissão</TableHead><TableHead>Pgto Previsto</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>{items.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{sn(c.vendedor_id)}</TableCell>
                  <TableCell>{fmtDate(c.data_recebimento)}</TableCell>
                  <TableCell>{formatMesLabel(c.mes_apuracao)}</TableCell>
                  <TableCell>{fmtBRL(c.base)}</TableCell>
                  <TableCell className="font-semibold">{fmtBRL(c.valor)} {c.manual_override && <Badge variant="secondary" className="ml-1 text-[9px]">manual</Badge>}</TableCell>
                  <TableCell>{fmtDate(c.pagamento_previsto)}</TableCell>
                  <TableCell><Badge variant="outline" className={c.status === "pago" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}>{c.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {c.status === "pendente" && canPay && <Button size="sm" variant="ghost" onClick={() => pay(c.id)} data-testid={`comm-pay-${c.id}`}><CheckCircle2 className="w-4 h-4"/></Button>}
                    {c.status === "pendente" && canEdit && <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`comm-edit-${c.id}`}><Pencil className="w-4 h-4"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`comm-delete-${c.id}`}><Trash2 className="w-4 h-4"/></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}{items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Sem comissões</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Comissão</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{sn(edit.vendedor_id)} — Recebimento {fmtDate(edit.data_recebimento)} — Base {fmtBRL(edit.base)}</div>
            <div>
              <Label className="text-xs font-semibold">Valor da Comissão (R$)</Label>
              <Input data-testid="comm-edit-valor" type="number" min="0" step="0.01" value={editValor} onChange={(e) => setEditValor(e.target.value)} className="mt-1"/>
              <p className="text-[10px] text-muted-foreground mt-1">Valor padrão calculado: {fmtBRL(edit.base * 0.20)} (20%). Pode diminuir ou zerar para anular.</p>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button onClick={saveEdit} data-testid="comm-edit-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pagar valor manual de comissão</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[10px] text-muted-foreground">
              Paga as comissões pendentes mais antigas do vendedor até esgotar o valor informado. Se o valor for maior
              que o total pendente, a diferença é registrada como adiantamento e abatida automaticamente das próximas comissões geradas.
            </p>
            <div>
              <Label className="text-xs font-semibold">Vendedor</Label>
              <SearchableSelect
                value={manualForm.vendedor_id}
                onValueChange={(v) => setManualForm(f => ({ ...f, vendedor_id: v }))}
                options={sellers.map(s => ({ value: s.id, label: s.nome }))}
                placeholder="Selecionar..."
                searchPlaceholder="Buscar vendedor..."
                testid="comm-manual-vendedor"
                className="mt-1"
              />
            </div>
            <div><Label className="text-xs font-semibold">Valor a pagar (R$)</Label><Input data-testid="comm-manual-valor" type="number" min="0.01" step="0.01" value={manualForm.valor} onChange={(e) => setManualForm(f => ({ ...f, valor: e.target.value }))} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Data do pagamento</Label><Input data-testid="comm-manual-data" type="date" value={manualForm.data} onChange={(e) => setManualForm(f => ({ ...f, data: e.target.value }))} className="mt-1"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button onClick={submitManual} data-testid="comm-manual-confirm">Confirmar Pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
